import React, { useEffect } from "react";
import {
  CircularProgress,
  Card,
  CardActions,
  CardContent,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from "@material-ui/core";
import { TimePicker, DatePicker } from "@material-ui/pickers";
import { useMutation, useQuery, useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useForm, Controller, useWatch } from "react-hook-form";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { html2json, json2html } from "html2json";
import "../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import { propertyDataArray } from "../../components/PropertyDataArray";
import { useAlert } from "../../components/Alert";
import Grid from "../../components/Grid";
import Text from "../../components/Text";
import LoadingModal from "../../components/LoadingModal";
import divisions from "../../divisions.json";
import DraftWysiwyg from "./DraftWysiwyg";
import UploadImage from "./UploadImage";

const GET_EXCLUSIVEPROPERTY = gql`
  query exclusiveProperty($id: Int!) {
    exclusiveProperty(id: $id) {
      id
      obj_court
      obj_str
      obj_share
      obj_caseno
      obj_another
      obj_time
      obj_date_ymd
      endedDate
      hiddenDate
      obj_date_hi
      obj_city
      obj_area
      obj_section
      obj_section_s
      obj_landno
      obj_address
      obj_link
      obj_margin
      obj_price
      obj_price_set
      obj_creditor
      obj_debtor
      obj_bulletin_data
      obj_remark
      obj_land_data
      obj_build_data
      obj_land_floor
      obj_build_floor
      obj_add_floor
      obj_s_floor
      obj_c_floor
      obj_total_floor
      obj_transfer
      obj_type
      obj_creat_date
      obj_stop
      obj_tender_num
      obj_tender
      obj_check_num
      obj_stop_link
      obj_source
      obj_marker
      obj_coordinates
      obj_request_num
      obj_cadastral
      obj_patterns
      pricePerBuildPing
      pricePerLandPing
      obj_sql
      obj_pyPDF
      phppdfjoin
      errorLog
      lastEditedBy
      images {
        id
        imageUrl
      }
      imageHasCreated
      source
      name
      rent
      deposit
      managementFee
      monthOfManagementFee
      parkingSpace
      houseAge
      indoor_floor
      room
      hall
      bathroom
      orientation
      youtubeUrl
      buildingStructure
      template
      description
      enable
    }
  }
`;

const GET_EXCLUSIVEPROPERTYTEMPLATES = gql`
  query exclusivePropertyTemplates($id: Int!) {
    exclusivePropertyTemplates(id: $id) {
      id
      name
      content
    }
  }
`;

const SAVE_EXCLUSIVEPROPERTY = gql`
  mutation saveExclusiveProperty(
    $exclusivePropertyInput: ExclusivePropertyInput!
  ) {
    saveExclusiveProperty(exclusivePropertyInput: $exclusivePropertyInput)
  }
`;

export default function CustomizeProperty({
  propertyId,
  franchiseId,
  onChangeType = () => {},
  onEdit = () => {},
}) {
  const Alert = useAlert();

  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      source: "出售",
      name: "",
      obj_court: "",
      obj_str: "",
      obj_share: "",
      obj_caseno: "",
      obj_time: "",
      obj_transfer: "",
      rent: "",
      deposit: "",
      obj_patterns: "",
      managementFee: "",
      monthOfManagementFee: "",
      obj_price: "",
      obj_margin: "",
      obj_city: "",
      obj_area: "",
      obj_address: "",
      parkingSpace: "",
      houseAge: "",
      obj_total_floor: "",
      obj_land_floor: "",
      indoor_floor: "",
      obj_s_floor: "",
      obj_add_floor: "",
      obj_c_floor: "",
      room: "",
      hall: "",
      bathroom: "",
      orientation: "",
      template: "",
      obj_date_ymd: null,
      endedDate: null,
      obj_date_hi: null,
      hiddenDate: "否",
      buildingStructure: "",
      youtubeUrl: "",
      description: EditorState.createEmpty(),
      images: [],
    },
  });
  const obj_date_ymd = useWatch({ control, name: "obj_date_ymd" });
  const source = useWatch({ control, name: "source" });
  const editorState = useWatch({ control, name: "description" });
  const images = useWatch({ control, name: "images" });
  const currentTemplate = useWatch({ control, name: "template" });
  const { objType, orientationArray, courtArray, transferArray } =
    propertyDataArray();
  const obj_city = useWatch({ control, name: "obj_city" });
  const divisionList = Object.keys(divisions);
  const subdistrictList = obj_city
    ? Object.keys(divisions[obj_city]["subdivisions"])
    : [];
  let descriptionContent;
  let contentState;

  const htmlFormat = (data) => {
    const json = html2json(data);
    Array.isArray(json.child) &&
      json.child.forEach((item) => {
        if (item.tag === "div") {
          item.tag = "img";
          if (
            item.attr &&
            Array.isArray(item.child) &&
            item.child.length === 1 &&
            item.child[0].attr &&
            Array.isArray(item.child[0].attr.style)
          ) {
            const style = item.attr.style + item.child[0].attr.style.join("");
            item.attr = {
              ...item.child[0].attr,
            };
            item.attr.style = style;
          }
        }
      });

    const html = json2html(json);
    return html;
  };

  const { data, loading: exclusivePropertyTemplatesLoading } = useQuery(
    GET_EXCLUSIVEPROPERTYTEMPLATES,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      variables: {
        id: franchiseId,
      },
      onError(error) {
        Alert.alert("通知", `${error}`, [
          {
            text: "確定",
            type: "ok",
          },
        ]);
      },
    }
  );

  const [getExclusiveProperty, { data: exclusivePropertyData, loading }] =
    useLazyQuery(GET_EXCLUSIVEPROPERTY, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onCompleted({ exclusiveProperty }) {
        if (exclusiveProperty) {
          const {
            lastEditedBy,
            pricePerLandPing,
            pricePerBuildPing,
            obj_check_num,
            obj_price_set,
            obj_tender_num,
            __typename,
            imageHasCreated,
            enable,
            images,
            obj_city,
            obj_area,
            description,
            template,
            hiddenDate,
            obj_date_hi,
            ...newExclusiveProperty
          } = exclusiveProperty;

          const divisionArray = Object.entries(divisions);
          const newImages = [];
          divisionArray.forEach((item) => {
            if (item[1].zhTW === obj_city) {
              setValue("obj_city", item[0]);
              const subdistrictArray = Object.entries(item[1]["subdivisions"]);
              subdistrictArray.forEach((item2) => {
                if (item2[1].zhTW === obj_area) {
                  setValue("obj_area", item2[1].zhTW);
                }
              });
            }
          });
          images.forEach((item) => {
            newImages.push(item.imageUrl);
            setValue("images", newImages);
          });

          if (!!description) {
            descriptionContent = htmlToDraft(
              description.replace(
                /<div style="text-align:center;"><img/g,
                '<div style="text-align:none;"><img'
              )
            );
            contentState = ContentState.createFromBlockArray(
              descriptionContent.contentBlocks,
              descriptionContent.entityMap
            );
            setValue(
              "description",
              EditorState.createWithContent(contentState)
            );
          }

          if (exclusiveProperty.source === null) setValue("source", "法拍");

          if (template !== null) setValue("template", "原物件範本");
          else setValue("template", "[空白範本標題]");

          setValue("hiddenDate", `${hiddenDate ? "是" : "否"}`);

          for (const key in newExclusiveProperty) {
            setValue(`${key}`, newExclusiveProperty[key] ?? null);
          }
          setTimeout(() => {
            setValue("obj_date_hi", obj_date_hi);
          }, 0);
        }
      },
    });
  const [saveExclusiveProperty, { loading: saveExclusivePropertyLoading }] =
    useMutation(SAVE_EXCLUSIVEPROPERTY, {
      onCompleted({ saveExclusiveProperty }) {
        if (saveExclusiveProperty) {
          return Alert.notice("儲存成功！");
        }
      },
      onError(error) {
        return Alert.notice(`${error}`);
      },
    });

  function _save(data) {
    const {
      obj_c_floor,
      obj_add_floor,
      obj_s_floor,
      indoor_floor,
      obj_land_floor,
      obj_total_floor,
      obj_build_floor,
      rent,
      deposit,
      managementFee,
      obj_price,
      obj_margin,
      obj_city,
      description,
      obj_date_hi,
      obj_creat_date,
      hiddenDate,
      ...input
    } = data;

    const exclusivePropertyInput = {
      obj_c_floor: Number(obj_c_floor),
      obj_add_floor: Number(obj_add_floor),
      obj_s_floor: Number(obj_s_floor),
      indoor_floor: Number(indoor_floor),
      obj_land_floor: Number(obj_land_floor),
      obj_total_floor: Number(obj_total_floor),
      obj_build_floor:
        obj_build_floor === "" ? 0 : Number(data.obj_build_floor),
      rent: Number(rent),
      deposit: Number(deposit),
      managementFee: Number(managementFee),
      obj_price: Number(obj_price),
      obj_margin: Number(obj_margin),
      obj_city: divisions[obj_city]["zhTW"],
      obj_date_hi: obj_date_hi === "" ? null : obj_date_hi,
      obj_creat_date: obj_creat_date === "" ? null : obj_creat_date,
      franchiseStoreId: franchiseId,
      description: draftToHtml(
        convertToRaw(description.getCurrentContent())
      ).replace(
        /<div style="text-align:none;"><img/g,
        '<div style="text-align:center;"><img'
      ),
      hiddenDate: hiddenDate === "是" ? true : false,
      ...input,
    };

    if (propertyId) exclusivePropertyInput.id = propertyId;

    saveExclusiveProperty({ variables: { exclusivePropertyInput } });
  }

  useEffect(() => {
    if (!!propertyId) {
      getExclusiveProperty({ variables: { id: propertyId } });
    }
  }, [propertyId]);

  useEffect(() => {
    if (currentTemplate !== "") {
      if (String(currentTemplate).includes("總店範本")) {
        descriptionContent =
          htmlFormat(`<h1></h1><div style="text-align:center;"><img src="/assets/images/Template/img1.jpg" alt="" style="height: 233px;width: 1600px"/></div>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: rgb(255,255,0);font-size: 48px;"><strong> ★★★《法拍國父》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-family: arial, helvetica, sans-serif;">●30多年來，標購自有全台各地大量資產，全部沒貸款， 展現為客戶交屋點交安全、安心保障『誠』跟『信』。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-family: arial, helvetica, sans-serif;">●台灣法拍代標、點交標準建立者。建請內政部做出代標、點交規範（89）內第字第8980474號、（91）內第字0910083161號。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: yellow;font-size: 48px;font-family: arial, helvetica, sans-serif;"><strong> ★★★《法拍龍頭》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-size: 28px;font-family: arial, helvetica, sans-serif;">●全國第一家通過ISO9001國際標準認證。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-size: 28px;font-family: arial, helvetica, sans-serif;">●全國100多家法拍屋加盟連鎖企業集團。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: yellow;font-size: 48px;font-family: arial, helvetica, sans-serif;"><strong> ★★★《法拍權威－教父團隊》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-size: 28px;font-family: arial, helvetica, sans-serif;">處理過無數次點交變不點交、</span><span style="font-size: 28px;">不點交、共有物及法拍各項複雜物件...等等。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-size: 28px;">全台各地成交無數客戶，</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="font-size: 28px;">包括律師、醫師、會計師、警察、法院司法人員、營造廠、建設公司、上市櫃公司...等，各大大小小公司與上班族。</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: yellow;font-family: arial, helvetica, sans-serif;"><strong> ★★★《媒體訪問》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img2.jpg" alt="" style="height: 560px;width: 1200px"/></div>   
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: yellow;font-family: arial, helvetica, sans-serif;"><strong> ★★★《專業講座》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img3.jpg" alt="" style="height: 277px;width: 1200px"/></div>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: yellow;font-family: arial, helvetica, sans-serif;"><strong> ★★★《得獎紀錄》★★★ </strong></span>&nbsp;&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img4.jpg" alt="" style="height: 691px;width: 1200px"/></div>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img5.jpg" alt="" style="height: 441px;width: 1000px"/></div>
          <h1 style="text-align:center;"><span style="color: rgb(255,160,122);font-size: 36px;">規模最大</span><span style="font-size: 36px;">｜</span><span style="color: rgb(255,215,0);font-size: 36px;">歷史最久</span><span style="font-size: 36px;">｜</span><span style="color: rgb(0,128,128);font-size: 36px;">交屋履約保證</span><span style="font-size: 36px;">｜</span><span style="color: rgb(51,102,255);font-size: 36px;">交屋風險承擔</span><span style="font-size: 36px;">｜</span><span style="color: rgb(255,51,153);font-size: 36px;">免騷擾保固</span><span style="font-size: 36px;">｜</span><span style="color: rgb(128,0,128);font-size: 36px;">ISO國際標準</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="background-color: rgb(0,255,255);font-size: 36px;"> 沒有不能點交的房子，只有不會點交的人！</span>&nbsp;&nbsp;</h1>
          <p>&nbsp;&nbsp;</p>
          <h1 style="text-align:center;"><span style="background-color: rgb(102,255,51);font-size: 36px;"> ※  首創『無法交屋、原價買回』※ </span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(255,0,0);font-size: 28px;">【保證產權清楚，保證交易安全，保證交屋迅速】</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(255,0,0);font-size: 28px;">【專業精準評估，競價落點分析，筆錄陷阱分析】</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(255,0,0);font-size: 28px;">【投標流程解說，排除瑕疵事故，銀行代墊尾款】</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(255,0,0);font-size: 28px;">【負責全程點交，點交無須出面，交屋風險承擔】</span>&nbsp;&nbsp;</h1>
          <p>&nbsp;&nbsp;</p>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img6.jpg" alt="" style="height: 250px;width: 400px"/></div>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(68,68,68);font-family: arial, helvetica, sans-serif;">～想知道更多相關訊息，記得立即按讚追蹤～</span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img7.jpg" alt="" style="height: 250px;width: 400px"/></div>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">～歡迎加入好友以便獲得第一手資訊～&nbsp;&nbsp;</h1>
          <p>&nbsp;&nbsp;</p>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;"><span style="color: rgb(255,0,0);background-color: rgb(255,255,224);font-size: 28px;">【全程點交到交屋，都不需出面。《一條龍服務》簡單、有保障。】</span>&nbsp;&nbsp;</h1>
          <p>&nbsp;&nbsp;</p>
          <h1 style="text-align:center;">&nbsp;</h1>
          <div style="text-align:center;"><img src="http://www.5137.com.tw/mail/ckeditor/plugins/smiley/images/lightbulb.gif" alt="enlightened" style="height: ;width: "/></div> 
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: rgb(255,215,0);font-size: 36px;"><strong> 請洽詢本專案經理人 </strong></span>&nbsp;&nbsp;</h1>
          <h1 style="text-align:center;">&nbsp;&nbsp;</h1>
          <p>&nbsp;&nbsp;</p>
          <h1 style="text-align:center;">&nbsp;</h1>
          <div style="text-align:center;"><img src="/assets/images/Template/img8.jpg" alt="" style="height: 522px;width: 500px"/></div>
          <h1 style="text-align:center;"><span style="color: rgb(0,0,255);background-color: rgb(255,215,0);font-size: 72px;"><strong> 富屋法拍屋企業集團 </strong></span>&nbsp;&nbsp;</h1>
          <p style="text-align:center;">&nbsp;</p>                 
      `);
      } else if (String(currentTemplate).includes("空白範本標題")) {
        descriptionContent = htmlFormat(`<p></p>`);
      } else if (String(currentTemplate).includes("原物件範本")) {
        descriptionContent = htmlFormat(
          exclusivePropertyData?.exclusiveProperty.description
        );
      } else {
        descriptionContent = htmlFormat(
          `${data?.exclusivePropertyTemplates
            .find((item) => item.name === currentTemplate)
            .content.replace(
              /<div style="text-align:center;"><img/g,
              '<div style="text-align:none;"><img'
            )}`
        );
      }
    }

    if (!!descriptionContent) {
      const contentBlock = htmlToDraft(descriptionContent, (nodeName, node) => {
        if (nodeName === "img" && node instanceof HTMLImageElement) {
          const entityConfig = {};
          entityConfig.src = node.getAttribute
            ? node.getAttribute("src") || node.src
            : node.src;
          entityConfig.alt = node.alt;
          entityConfig.height = node.style.height;
          entityConfig.width = node.style.width;
          if (node.style.float) {
            entityConfig.alignment = node.style.float;
          } else {
            if (node.style.textAlign) {
              entityConfig.alignment = node.style.textAlign;
            }
          }

          return {
            type: "IMAGE",
            mutability: "MUTABLE",
            data: entityConfig,
          };
        }
      });

      contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
        contentBlock.entityMap
      );
      setValue("description", EditorState.createWithContent(contentState));
    }
  }, [currentTemplate]);

  if (loading || exclusivePropertyTemplatesLoading) {
    return (
      <Grid container item justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <Card>
        <LoadingModal loading={saveExclusivePropertyLoading} />
        <CardContent>
          <Grid container justifyContent="flex-end">
            {propertyId ? (
              <Button variant="contained" color="primary" onClick={onEdit}>
                返回
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={onChangeType}
              >
                由查詢功能新增物件
              </Button>
            )}
          </Grid>
          <Grid container spacing={1}>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>物件來源：</Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="source"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{
                        maxHeight: "40px",
                        overflow: "hidden",
                      }}
                    >
                      {propertyId ? (
                        <MenuItem value="法拍">法拍</MenuItem>
                      ) : null}
                      <MenuItem value="出售">出售</MenuItem>
                      <MenuItem value="出租">出租</MenuItem>
                      <MenuItem value="頂讓">頂讓</MenuItem>
                      <MenuItem value="建案">建案</MenuItem>
                    </Select>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>物件名稱：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="name"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {source === "法拍" && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    // border: "1px solid",
                  }}
                >
                  <Grid item xs={2} sm={2}>
                    <Typography>拍賣法院：</Typography>
                  </Grid>
                  <Grid item xs={5} sm={5}>
                    <Controller
                      name="obj_court"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Select
                          variant="outlined"
                          fullWidth
                          {...field}
                          style={{
                            height: "40px",
                            overflow: "hidden",
                          }}
                        >
                          {courtArray.map((item) => {
                            return (
                              <MenuItem value={item.value}>
                                {item.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    // border: "1px solid",
                  }}
                >
                  <Grid item xs={2} sm={2}>
                    <Typography>案號：</Typography>
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <Controller
                      name="obj_str"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Grid
                          item
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            // border: "1px solid",
                          }}
                        >
                          <Grid item>
                            <TextField
                              {...field}
                              error={error}
                              helperText={error && error.message}
                            />
                          </Grid>
                          <Grid item>
                            <Typography>年</Typography>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} style={{ paddingLeft: "4px" }}>
                    <Controller
                      name="obj_share"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Grid
                          item
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            // border: "1px solid",
                          }}
                        >
                          <Grid item>
                            <TextField
                              {...field}
                              error={error}
                              helperText={error && error.message}
                            />
                          </Grid>
                          <Grid item>
                            <Typography>字</Typography>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} style={{ paddingLeft: "4px" }}>
                    <Controller
                      name="obj_caseno"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Grid
                          item
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            // border: "1px solid",
                          }}
                        >
                          <Grid item>
                            <TextField
                              {...field}
                              error={error}
                              helperText={error && error.message}
                            />
                          </Grid>
                          <Grid item>
                            <Typography>號</Typography>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    // border: "1px solid",
                  }}
                >
                  <Grid item xs={2} sm={2}>
                    <Typography>拍次：</Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="obj_time"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Grid
                          item
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            // border: "1px solid",
                          }}
                        >
                          <Grid item>
                            <TextField
                              {...field}
                              error={error}
                              helperText={error && error.message}
                            />
                          </Grid>
                          <Grid item>
                            <Typography>拍</Typography>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    // border: "1px solid",
                  }}
                >
                  <Grid item xs={2} sm={2}>
                    <Typography>點交情形：</Typography>
                  </Grid>
                  <Grid item xs={2} sm={2}>
                    <Controller
                      name="obj_transfer"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Select
                          variant="outlined"
                          fullWidth
                          {...field}
                          style={{ maxHeight: "40px", overflow: "hidden" }}
                        >
                          {transferArray.map((item) => {
                            return (
                              <MenuItem value={item.value}>
                                {item.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>租金：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="rent"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          disabled={source !== "出租"}
                          style={{
                            backgroundColor:
                              source !== "出租" ? "#e0e0e0" : "#fff",
                          }}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>元／月</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>押金：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="deposit"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          disabled={source !== "出租"}
                          style={{
                            backgroundColor:
                              source !== "出租" ? "#e0e0e0" : "#fff",
                          }}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>元／月</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>物件類型：</Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="obj_patterns"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{ maxHeight: "40px", overflow: "hidden" }}
                    >
                      {objType.map((item) => {
                        return <MenuItem value={item}>{item}</MenuItem>;
                      })}
                    </Select>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>管理費：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="managementFee"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>元／</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="monthOfManagementFee"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>月</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>{`${
                  source === "法拍" ? "底價：" : "價格："
                }`}</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_price"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>萬</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            {source === "法拍" && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    // border: "1px solid",
                  }}
                >
                  <Grid item xs={2} sm={2}>
                    <Typography>保證金：</Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="obj_margin"
                      control={control}
                      // rules={{
                      //   required: "請填入姓名",
                      // }}
                      render={({ field, fieldState: { error } }) => (
                        <Grid
                          item
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "flex-end",
                            // border: "1px solid",
                          }}
                        >
                          <Grid item>
                            <TextField
                              {...field}
                              error={error}
                              helperText={error && error.message}
                            />
                          </Grid>
                          <Grid item>
                            <Typography>元</Typography>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={1} sm={1}>
                <Typography>地址：</Typography>
              </Grid>
              <Grid item xs={1} sm={1}>
                <Controller
                  name="obj_city"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{ maxHeight: "40px", overflow: "hidden" }}
                    >
                      {divisionList.map((item) => (
                        <MenuItem value={item}>
                          {divisions[item]["zhTW"]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
              <Grid item xs={1} sm={1} style={{ paddingLeft: "4px" }}>
                <Controller
                  name="obj_area"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{ maxHeight: "40px", overflow: "hidden" }}
                    >
                      {subdistrictList.map((item) => (
                        <MenuItem
                          value={
                            divisions[obj_city]["subdivisions"][item]["zhTW"]
                          }
                        >
                          {divisions[obj_city]["subdivisions"][item]["zhTW"]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6} style={{ paddingLeft: "4px" }}>
                <Controller
                  name="obj_address"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>停車位：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="parkingSpace"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>屋齡：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="houseAge"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>年</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>總建坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_total_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>地坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_land_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>室內坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="indoor_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>附建坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_s_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>增建坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_add_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>公設坪：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_c_floor"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>坪</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>格局：</Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="room"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>房</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>

              <Grid item xs={2} sm={2} style={{ paddingLeft: "4px" }}>
                <Controller
                  name="hall"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>廳</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>

              <Grid item xs={2} sm={2} style={{ paddingLeft: "4px" }}>
                <Controller
                  name="bathroom"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Grid
                      item
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        // border: "1px solid",
                      }}
                    >
                      <Grid item>
                        <TextField
                          {...field}
                          error={error}
                          helperText={error && error.message}
                        />
                      </Grid>
                      <Grid item>
                        <Typography>衛</Typography>
                      </Grid>
                    </Grid>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>座向：</Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="orientation"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{ maxHeight: "40px", overflow: "hidden" }}
                    >
                      {orientationArray.map((item) => {
                        return <MenuItem value={item}>{item}</MenuItem>;
                      })}
                    </Select>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={1} sm={1}>
                <Typography>選擇範本：</Typography>
              </Grid>
              <Grid item xs={2} sm={2}>
                <Controller
                  name="template"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      variant="outlined"
                      fullWidth
                      {...field}
                      style={{
                        maxHeight: "40px",
                        overflow: "hidden",
                      }}
                    >
                      {!!propertyId && (
                        <MenuItem value={"原物件範本"}>{"原物件範本"}</MenuItem>
                      )}
                      <MenuItem value={"總店範本"}>{"總店範本"}</MenuItem>
                      <MenuItem value={"[空白範本標題]"}>
                        {"[空白範本標題]"}
                      </MenuItem>
                      {data?.exclusivePropertyTemplates?.map((item) => (
                        <MenuItem value={item.name}>{item.name}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>{`${
                  source === "法拍" ? "拍賣日期：" : "刊登日期："
                }`}</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="obj_date_ymd"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <DatePicker
                        autoOk={true}
                        {...field}
                        format={"yyyy-MM-dd "}
                      />

                      <Controller
                        name="hiddenDate"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <Grid container spacing={1} alignItems="center">
                            <Grid item>
                              <Typography>日期隱藏：</Typography>
                            </Grid>
                            <Grid item>
                              <RadioGroup
                                row
                                value={field.value}
                                onChange={field.onChange}
                              >
                                <FormControlLabel
                                  value="是"
                                  control={<Radio />}
                                  label="是"
                                />
                                <FormControlLabel
                                  value="否"
                                  control={<Radio />}
                                  label="否"
                                />
                              </RadioGroup>
                            </Grid>
                          </Grid>
                        )}
                      />
                    </>
                  )}
                />
              </Grid>
              <Grid
                item
                xs={source === "法拍" ? 2 : null}
                sm={source === "法拍" ? 2 : null}
                style={{ paddingLeft: "4px" }}
              >
                <Controller
                  name={source === "法拍" ? "obj_date_hi" : "endedDate"}
                  control={control}
                  render={({ field, fieldState: { error } }) =>
                    source === "法拍" ? (
                      <TimePicker {...field} format={"HH:mm"} />
                    ) : (
                      <DatePicker
                        autoOk={true}
                        {...field}
                        format={"yyyy-MM-dd "}
                        minDate={obj_date_ymd}
                      />
                    )
                  }
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>建物結構：</Typography>
              </Grid>
              <Grid item>
                <Controller
                  name="buildingStructure"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                // border: "1px solid",
              }}
            >
              <Grid item xs={2} sm={2}>
                <Typography>YouTube影片網址：</Typography>
              </Grid>
              <Grid item xs={6} sm={6}>
                <Controller
                  name="youtubeUrl"
                  control={control}
                  // rules={{
                  //   required: "請填入姓名",
                  // }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1} justify="center">
            <Grid item>
              <Text type="h4">上傳物件照片</Text>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Controller
              name="images"
              control={control}
              // rules={{
              //   required: "請填入網頁標題",
              // }}
              render={({ field, fieldState: { error } }) => (
                <UploadImage images={images} onChange={field.onChange} />
              )}
            />
          </Grid>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={1} justify="center">
            <Grid item>
              <Text type="h4">自訂物件說明</Text>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Controller
              name="description"
              control={control}
              // rules={{
              //   required: "請填入網頁標題",
              // }}
              render={({ field, fieldState: { error } }) => (
                <DraftWysiwyg
                  editorState={editorState}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
        </CardContent>
        <Divider />
        <CardActions>
          <Grid container justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(_save)}
            >
              儲存
            </Button>
          </Grid>
        </CardActions>
      </Card>
    );
  }
}
